<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle } from 'lucide-vue-next'
import BBtn from '@lib/components/base/BBtn.vue'
import BInput from '@lib/components/base/BInput.vue'
import BSelect from '@lib/components/base/BSelect.vue'
import type { LookupOption, TicketsetLookupOption } from '@/composables/useLookups'
import { FIELDS, type ColumnMapping, type FieldSource, type MappingSuggestion, type ParsedTable } from './import'
import { validateFixedValue } from './import'

// Mapovací krok: vlevo 17 cílových polí, vpravo volba zdroje.
//
// Návrhy pod prahem jistoty se ZÁMĚRNĚ nepředvyplňují (prázdné pole, které
// uživatel vidí, je levnější než tiše špatně namapovaný sloupec). Sada a cena
// se nepředvyplňují nikdy — klient je obvykle chce zvlášť.
const props = defineProps<{
    table: ParsedTable
    suggestions: Record<string, MappingSuggestion>
    modelValue: ColumnMapping
    eventOptions: LookupOption[]
    ticketsetOptions: TicketsetLookupOption[]
    selectedEventId: string | null
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: ColumnMapping): void
    (e: 'update:hasHeader', value: boolean): void
    (e: 'update:sheet', value: string): void
    (e: 'update:selectedEventId', value: string | null): void
    (e: 'update:skipRows', value: number): void
    (e: 'update:skipTrailingRows', value: number): void
    /** Uživatel potvrdil, že odhad hlavičky je správný — bere se jako jistota. */
    (e: 'confirmHeader'): void
}>()

const { t } = useI18n()

type SourceKind = 'column' | 'value' | 'existing' | 'empty'

const fixedValues = ref<Record<string, string>>({})

watch(
    () => props.modelValue,
    m => {
        for (const [key, source] of Object.entries(m)) {
            if (source?.kind === 'value' && fixedValues.value[key] === undefined) {
                fixedValues.value[key] = source.value
            }
        }
    },
    { immediate: true, deep: true }
)

const columnCount = computed(() =>
    props.table.rows.reduce((max, r) => Math.max(max, r.length), props.table.headers?.length ?? 0)
)

const columnOptions = computed(() =>
    Array.from({ length: columnCount.value }, (_, i) => ({
        value: i,
        label: props.table.headers?.[i]
            ? `${i + 1}. ${props.table.headers[i]}`
            : t('tickets.import.mapping.columnN', { n: i + 1 })
    }))
)

const sheetOptions = computed(() => (props.table.sheetNames ?? []).map(name => ({ value: name, label: name })))

/**
 * Ukázka zdroje včetně přeskočených řádků — uživatel musí vidět, co odřezává,
 * jinak by „ignorovat X řádků" bylo střílení naslepo.
 *
 * Každý řádek nese roli, aby šel v tabulce odlišit.
 */
type SampleRow = {
    cells: string[]
    role: 'skipped' | 'header' | 'data' | 'gap'
    sourceRow: number
}

const sampleRows = computed<SampleRow[]>(() => {
    const t = props.table
    const out: SampleRow[] = []

    t.rawPreview.slice(0, t.skipRows).forEach((cells, i) => {
        out.push({ cells, role: 'skipped', sourceRow: i + 1 })
    })

    if (t.headers) {
        out.push({ cells: t.headers, role: 'header', sourceRow: t.skipRows + 1 })
    }

    const HEAD = 5
    t.rows.slice(0, HEAD).forEach((cells, i) => {
        out.push({ cells, role: 'data', sourceRow: t.firstDataRow + i })
    })

    // Konec souboru se ukazuje vždy — ať je vidět, čím soubor doopravdy končí.
    // Vynechají se jen řádky, které už jsou v hlavě ukázky výš.
    const lastShownInHead = out.length ? out[out.length - 1].sourceRow : 0

    t.rawTailPreview.forEach((cells, i) => {
        const sourceRow = t.totalSourceRows - t.rawTailPreview.length + i + 1
        if (sourceRow <= lastShownInHead) return

        // Předěl vložit jen když mezi hlavou a ocasem něco chybí.
        if (sourceRow > lastShownInHead + 1 && !out.some(r => r.role === 'gap')) {
            out.push({ cells: [], role: 'gap', sourceRow: 0 })
        }

        const isTrimmed = sourceRow > t.totalSourceRows - t.skipTrailingRows
        out.push({ cells, role: isTrimmed ? 'skipped' : 'data', sourceRow })
    })

    return out
})

function sampleRowClass(role: SampleRow['role']): string {
    if (role === 'skipped') return 'bg-gray-100 text-gray-400 line-through'
    if (role === 'header') return 'bg-teal-l font-semibold'
    return ''
}

// --- Existující sady a ceny ve vybrané akci -------------------------------

/** Sady patřící vybrané akci. Bez akce je nabídka prázdná. */
const eventTicketsets = computed(() =>
    props.selectedEventId ? props.ticketsetOptions.filter(ts => ts.eventId === props.selectedEventId) : []
)

const ticketsetChoices = computed(() =>
    eventTicketsets.value.map(ts => ({ value: ts.name, label: ts.name }))
)

const selectedTicketset = computed(() => {
    const source = props.modelValue.ticketsetName
    if (source?.kind !== 'existing') return null
    return eventTicketsets.value.find(ts => ts.name === source.value) ?? null
})

/** Cenové hladiny vybrané sady — bez sady nemá výběr z čeho brát. */
const priceChoices = computed(() =>
    (selectedTicketset.value?.prices ?? []).map(p => ({ value: p.name, label: p.name }))
)

/** Volba „existující" dává smysl jen tam, kde je z čeho vybírat. */
function canPickExisting(key: string): boolean {
    if (key === 'ticketsetName') return props.selectedEventId !== null
    if (key === 'priceName') return selectedTicketset.value !== null
    return false
}

function existingChoices(key: string) {
    return key === 'ticketsetName' ? ticketsetChoices.value : priceChoices.value
}

function existingValue(key: string): string | null {
    const s = props.modelValue[key]
    return s?.kind === 'existing' ? s.value : null
}

function setExisting(key: string, name: unknown) {
    if (typeof name !== 'string') return

    if (key === 'ticketsetName') {
        const ts = eventTicketsets.value.find(x => x.name === name)
        const next: ColumnMapping = {
            ...props.modelValue,
            ticketsetName: { kind: 'existing', value: name, bzucoId: ts?.bzucoId ?? null }
        }
        // Cena patří ke konkrétní sadě — po změně sady je stará volba neplatná.
        if (props.modelValue.priceName?.kind === 'existing') {
            next.priceName = { kind: 'empty' }
        }
        emit('update:modelValue', next)
        return
    }

    const price = selectedTicketset.value?.prices.find(p => p.name === name)
    setSource(key, { kind: 'existing', value: name, bzucoId: price?.bzucoId ?? null })
}

// --- Obecné ovládání zdrojů ----------------------------------------------

function kindOf(key: string): SourceKind {
    return (props.modelValue[key]?.kind ?? 'empty') as SourceKind
}

function columnOf(key: string): number | null {
    const s = props.modelValue[key]
    return s?.kind === 'column' ? s.index : null
}

/** Náhled prvních hodnot sloupce, aby šlo mapování ověřit očima. */
function previewOf(key: string): string {
    const index = columnOf(key)
    if (index === null) return ''
    return props.table.rows
        .slice(0, 3)
        .map(r => r[index] ?? '')
        .filter(v => v !== '')
        .join(', ')
}

function errorOf(key: string): string | null {
    if (kindOf(key) !== 'value') return null
    return validateFixedValue(key, fixedValues.value[key] ?? '')
}

/** Sloupce zabrané jiným polem — jeden zdroj nesmí sytit dvě pole. */
function takenBy(key: string): Set<number> {
    const taken = new Set<number>()
    for (const [k, s] of Object.entries(props.modelValue)) {
        if (k !== key && s?.kind === 'column') taken.add(s.index)
    }
    return taken
}

function optionsFor(key: string) {
    const taken = takenBy(key)
    return columnOptions.value.filter(o => !taken.has(o.value) || columnOf(key) === o.value)
}

function setSource(key: string, source: FieldSource) {
    emit('update:modelValue', { ...props.modelValue, [key]: source })
}

function setKind(key: string, kind: SourceKind) {
    if (kind === 'column') {
        const taken = takenBy(key)
        const free = columnOptions.value.find(o => !taken.has(o.value))
        setSource(key, free ? { kind: 'column', index: free.value } : { kind: 'empty' })
    } else if (kind === 'value') {
        setSource(key, { kind: 'value', value: fixedValues.value[key] ?? '' })
    } else if (kind === 'existing') {
        setSource(key, { kind: 'existing', value: '', bzucoId: null })
    } else {
        setSource(key, { kind: 'empty' })
    }
}

function setColumn(key: string, index: unknown) {
    if (typeof index === 'number') setSource(key, { kind: 'column', index })
}

function setValue(key: string, value: string) {
    fixedValues.value[key] = value
    setSource(key, { kind: 'value', value })
}

/** ID sady/ceny se při výběru existující entity doplní automaticky. */
function isAutoFilled(key: string): boolean {
    if (key === 'ticketsetId') return props.modelValue.ticketsetName?.kind === 'existing'
    if (key === 'priceId') return props.modelValue.priceName?.kind === 'existing'
    return false
}

function autoFilledValue(key: string): string {
    const source = key === 'ticketsetId' ? props.modelValue.ticketsetName : props.modelValue.priceName
    return source?.kind === 'existing' && source.bzucoId != null ? String(source.bzucoId) : '—'
}

const CONFIDENT_REASONS = ['alias', 'fuzzy']

function reasonLabel(key: string): string | null {
    const s = props.suggestions[key]
    if (!s || s.source.kind === 'empty') return null
    if (columnOf(key) !== (s.source as { index: number }).index) return null
    return t(`tickets.import.mapping.reason.${s.reason}`)
}

function reasonClass(key: string): string {
    const s = props.suggestions[key]
    return s && CONFIDENT_REASONS.includes(s.reason) ? 'text-green' : 'text-gray-500'
}
</script>

<template>
    <div class="bg-white rounded-12 border border-gray-200 overflow-hidden">
        <!-- Detekce: vše přepnutelné, nic se nehádá natvrdo -->
        <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center text-xs">
            <!-- Aplikuje se PŘED detekcí hlavičky — kvůli exportům s titulkem nad ní -->
            <label class="flex items-center gap-2">
                <span>{{ t('tickets.import.mapping.skipRows') }}</span>
                <input
                    type="number"
                    min="0"
                    :max="table.skipRows + table.rows.length"
                    :value="table.skipRows"
                    class="w-16 px-2 py-1 border border-gray-300 rounded-4 tabular-nums"
                    @change="emit('update:skipRows', Number(($event.target as HTMLInputElement).value) || 0)"
                />
            </label>

            <span class="mx-4 w-px h-4 bg-gray-300" />

            <!-- Souhrny („CELKEM“) na konci exportu -->
            <label class="flex items-center gap-2">
                <span>{{ t('tickets.import.mapping.skipTrailingRows') }}</span>
                <input
                    type="number"
                    min="0"
                    :max="table.skipTrailingRows + table.rows.length"
                    :value="table.skipTrailingRows"
                    class="w-16 px-2 py-1 border border-gray-300 rounded-4 tabular-nums"
                    @change="emit('update:skipTrailingRows', Number(($event.target as HTMLInputElement).value) || 0)"
                />
            </label>

            <span class="mx-4 w-px h-4 bg-gray-300" />

            <label class="flex items-center gap-2">
                <input
                    type="checkbox"
                    :checked="table.hasHeader"
                    @change="emit('update:hasHeader', ($event.target as HTMLInputElement).checked)"
                />
                <span>{{ t('tickets.import.mapping.hasHeader') }}</span>
            </label>

            <template v-if="sheetOptions.length > 1">
                <span class="mx-4 w-px h-4 bg-gray-300" />
                <label class="flex items-center gap-2">
                    <span>{{ t('tickets.import.mapping.sheet') }}</span>
                    <BSelect
                        :model-value="table.activeSheet"
                        :options="sheetOptions"
                        option-label="label"
                        option-value="value"
                        :show-clear="false"
                        @update:model-value="emit('update:sheet', $event as string)"
                    />
                </label>
            </template>

            <span class="mx-4 w-px h-4 bg-gray-300" />

            <span v-if="table.delimiter" class="text-gray-500">
                {{ t('tickets.import.mapping.delimiter') }}:
                <code class="font-mono">{{ table.delimiter === '\t' ? 'TAB' : table.delimiter }}</code>
            </span>
            <span v-if="table.delimiter" class="mx-3 text-gray-300">·</span>
            <span class="text-gray-500">{{ t('tickets.import.mapping.encoding') }}: {{ table.encoding }}</span>

            <span class="ml-auto font-semibold text-ink">{{
                t('tickets.import.rowCount', { n: table.rows.length })
            }}</span>
        </div>

        <!-- Ukázka zdroje: ať je vidět, co soubor obsahuje (a jestli má hlavičku) -->
        <div class="px-4 py-3 border-b border-gray-200 bg-white">
            <p class="text-xs font-semibold text-gray-600 mb-1.5">{{ t('tickets.import.mapping.sampleTitle') }}</p>
            <div class="overflow-x-auto border border-gray-200 rounded-8">
                <table class="text-xs whitespace-nowrap font-mono">
                    <tbody>
                        <template v-for="(row, ri) in sampleRows" :key="ri">
                            <!-- Předěl mezi začátkem a koncem souboru -->
                            <tr v-if="row.role === 'gap'" class="border-b border-gray-200">
                                <td colspan="99" class="px-2 py-1 text-center text-gray-400 select-none">⋯</td>
                            </tr>
                            <tr
                                v-else
                                class="border-b border-gray-200 last:border-b-0"
                                :class="sampleRowClass(row.role)"
                            >
                            <td class="px-2 py-1 text-gray-400 tabular-nums select-none border-r border-gray-200">
                                {{ row.sourceRow }}
                            </td>
                            <td class="px-2 py-1 text-gray-400 select-none border-r border-gray-200 no-underline">
                                <template v-if="row.role === 'skipped'">
                                    {{ t('tickets.import.mapping.skippedRowLabel') }}
                                </template>
                                <template v-else-if="row.role === 'header'">
                                    {{ t('tickets.import.mapping.headerRowLabel') }}
                                </template>
                            </td>
                            <td v-for="(cell, ci) in row.cells" :key="ci" class="px-2 py-1 max-w-48 truncate">
                                {{ cell || '—' }}
                            </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Nejistá detekce hlavičky: uživatel musí rozhodnout, nehádáme -->
        <div
            v-if="table.headerConfidence === 'low'"
            class="px-4 py-3 border-b border-gray-200 bg-amber-50 flex items-start gap-3 text-xs"
        >
            <AlertTriangle class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div class="flex-1 min-w-0">
                <p class="font-semibold text-amber-900">
                    {{
                        table.hasHeader
                            ? t('tickets.import.mapping.headerUnsureAssumedHeader')
                            : t('tickets.import.mapping.headerUnsureAssumedData')
                    }}
                </p>
                <p class="text-amber-800 mt-0.5">{{ t('tickets.import.mapping.headerUnsureHint') }}</p>
            </div>
            <BBtn severity="secondary" size="small" class="shrink-0" @click="emit('confirmHeader')">
                {{ t('tickets.import.mapping.headerConfirm') }}
            </BBtn>
        </div>

        <!-- Akce: musí být zvolená dřív než sada, protože z ní plyne nabídka sad -->
        <div class="px-4 py-3 border-b border-gray-200 bg-white">
            <label class="block max-w-md">
                <span class="text-xs font-semibold text-gray-600 mb-1 block">
                    {{ t('tickets.import.event') }} <span class="text-red">*</span>
                </span>
                <BSelect
                    :model-value="selectedEventId"
                    :options="eventOptions"
                    option-label="name"
                    option-value="id"
                    :placeholder="t('tickets.import.eventPlaceholder')"
                    :filter="true"
                    :invalid="selectedEventId === null"
                    @update:model-value="emit('update:selectedEventId', ($event as string) ?? null)"
                />
                <span v-if="selectedEventId === null" class="text-xs text-red mt-1 block">
                    {{ t('tickets.import.eventRequired') }}
                </span>
            </label>
        </div>

        <!-- 17 cílových polí -->
        <div class="divide-y divide-gray-200">
            <div
                v-for="field in FIELDS"
                :key="field.key"
                class="px-4 py-3 flex flex-col md:flex-row md:items-start gap-2 md:gap-4"
            >
                <div class="md:w-52 shrink-0 pt-1.5">
                    <span class="text-sm font-medium text-ink">{{
                        t(`tickets.import.preview.cols.${field.key}`)
                    }}</span>
                    <span v-if="field.required" class="text-red ml-1">*</span>
                </div>

                <div class="flex-1 min-w-0">
                    <!-- ID sady/ceny se doplní z vybrané entity — zamčené -->
                    <div v-if="isAutoFilled(field.key)" class="text-xs text-gray-600 pt-1.5">
                        <span class="inline-block px-2 py-1 rounded-4 bg-gray-100 font-mono">{{
                            autoFilledValue(field.key)
                        }}</span>
                        <span class="ml-2 text-gray-500">{{ t('tickets.import.mapping.autoFromExisting') }}</span>
                    </div>

                    <template v-else>
                        <div class="flex flex-wrap items-center gap-3 text-xs">
                            <label class="flex items-center gap-1.5">
                                <input
                                    type="radio"
                                    :checked="kindOf(field.key) === 'column'"
                                    @change="setKind(field.key, 'column')"
                                />
                                <span>{{ t('tickets.import.mapping.fromColumn') }}</span>
                            </label>
                            <label v-if="field.allowFixedValue" class="flex items-center gap-1.5">
                                <input
                                    type="radio"
                                    :checked="kindOf(field.key) === 'value'"
                                    @change="setKind(field.key, 'value')"
                                />
                                <span>{{ t('tickets.import.mapping.fixedValue') }}</span>
                            </label>
                            <label
                                v-if="field.key === 'ticketsetName' || field.key === 'priceName'"
                                class="flex items-center gap-1.5"
                                :class="canPickExisting(field.key) ? '' : 'opacity-50'"
                            >
                                <input
                                    type="radio"
                                    :checked="kindOf(field.key) === 'existing'"
                                    :disabled="!canPickExisting(field.key)"
                                    @change="setKind(field.key, 'existing')"
                                />
                                <span>{{
                                    field.key === 'ticketsetName'
                                        ? t('tickets.import.mapping.existingTicketset')
                                        : t('tickets.import.mapping.existingPrice')
                                }}</span>
                            </label>
                            <label v-if="!field.required" class="flex items-center gap-1.5">
                                <input
                                    type="radio"
                                    :checked="kindOf(field.key) === 'empty'"
                                    @change="setKind(field.key, 'empty')"
                                />
                                <span>{{ t('tickets.import.mapping.empty') }}</span>
                            </label>
                        </div>

                        <p
                            v-if="field.key === 'ticketsetName' && !canPickExisting(field.key)"
                            class="text-xs text-gray-500 mt-1"
                        >
                            {{ t('tickets.import.mapping.pickEventFirst') }}
                        </p>
                        <p
                            v-else-if="field.key === 'priceName' && !canPickExisting(field.key)"
                            class="text-xs text-gray-500 mt-1"
                        >
                            {{ t('tickets.import.mapping.pickTicketsetFirst') }}
                        </p>

                        <div v-if="kindOf(field.key) === 'column'" class="mt-1.5 max-w-md">
                            <BSelect
                                :model-value="columnOf(field.key)"
                                :options="optionsFor(field.key)"
                                option-label="label"
                                option-value="value"
                                :placeholder="t('tickets.import.mapping.selectColumn')"
                                :invalid="field.required && columnOf(field.key) === null"
                                :show-clear="false"
                                @update:model-value="setColumn(field.key, $event)"
                            />
                            <p v-if="previewOf(field.key)" class="text-xs text-gray-500 mt-1 truncate">
                                {{ t('tickets.import.mapping.preview') }}: {{ previewOf(field.key) }}
                            </p>
                            <p v-if="reasonLabel(field.key)" class="text-xs mt-0.5" :class="reasonClass(field.key)">
                                {{ reasonLabel(field.key) }}
                            </p>
                        </div>

                        <div v-else-if="kindOf(field.key) === 'existing'" class="mt-1.5 max-w-md">
                            <BSelect
                                :model-value="existingValue(field.key)"
                                :options="existingChoices(field.key)"
                                option-label="label"
                                option-value="value"
                                :placeholder="t('tickets.import.mapping.selectExisting')"
                                :filter="true"
                                :invalid="!existingValue(field.key)"
                                :show-clear="false"
                                @update:model-value="setExisting(field.key, $event)"
                            />
                            <p v-if="!existingChoices(field.key).length" class="text-xs text-gray-500 mt-1">
                                {{ t('tickets.import.mapping.noExisting') }}
                            </p>
                        </div>

                        <div v-else-if="kindOf(field.key) === 'value'" class="mt-1.5 max-w-md">
                            <BInput
                                :model-value="fixedValues[field.key] ?? ''"
                                :placeholder="t('tickets.import.mapping.valuePlaceholder')"
                                :invalid="!!errorOf(field.key)"
                                @update:model-value="setValue(field.key, $event)"
                            />
                            <p v-if="errorOf(field.key)" class="text-xs text-red mt-1">{{ errorOf(field.key) }}</p>
                            <p v-else class="text-xs text-gray-500 mt-1">
                                {{ t('tickets.import.mapping.appliesToAll') }}
                            </p>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
